import React, { useEffect, useState, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";
import { CreditCard } from "lucide-react";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
  PaymentElement,
} from "@stripe/react-stripe-js";
import { Translator } from "./translator";
import axios from "axios";

const BASE = "https://xmrhe4snn1.execute-api.eu-north-1.amazonaws.com";
const stripePromise = loadStripe(
  "pk_live_x6KA4EqDe3erfBuncVb5mVdy00z1vrAmRY"
);

const CheckoutForm = ({ totalAmount, userEmail }) => {
  const stripe = useStripe();
  const elements = useElements();
  const { translateText, lang } = Translator();

  // ---------- i18n: fixed IT / EN / FR, Translator for others ----------
  const baseTextByLangForm = useMemo(
    () => ({
      it: {
        text2: "Errore durante il pagamento.",
        text3: "Nome",
        text4: "Cognome",
        text5: "Indirizzo",
        text6: "Città",
        text7: "Provincia",
        text8: "CAP",
        text9: "Stato",
        text10: "Note",
        text11: "Totale",
        text12: "Paga con",
        text13: "Processando...",
        text14: "Paga",
      },
      en: {
        text2: "Error during payment.",
        text3: "First name",
        text4: "Last name",
        text5: "Address",
        text6: "City",
        text7: "Province / State",
        text8: "ZIP / Postal code",
        text9: "Country",
        text10: "Notes",
        text11: "Total",
        text12: "Pay with",
        text13: "Processing...",
        text14: "Pay",
      },
      fr: {
        text2: "Erreur lors du paiement.",
        text3: "Prénom",
        text4: "Nom",
        text5: "Adresse",
        text6: "Ville",
        text7: "Province / État",
        text8: "Code postal",
        text9: "Pays",
        text10: "Notes",
        text11: "Total",
        text12: "Payer avec",
        text13: "Traitement en cours...",
        text14: "Payer",
      },
    }),
    []
  );

  const [tForm, setTForm] = useState(
    baseTextByLangForm[lang] || baseTextByLangForm.en
  );

  useEffect(() => {
    if (lang === "it" || lang === "en" || lang === "fr") {
      setTForm(baseTextByLangForm[lang] || baseTextByLangForm.en);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const source = baseTextByLangForm.it;
        const entries = Object.entries(source);
        const translatedEntries = await Promise.all(
          entries.map(async ([key, value]) => {
            const out = await translateText(value);
            return [key, out || value];
          })
        );
        if (!cancelled) {
          setTForm(Object.fromEntries(translatedEntries));
        }
      } catch {
        if (!cancelled) {
          setTForm(baseTextByLangForm.en);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [lang, translateText, baseTextByLangForm]);

  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    nome: "",
    cognome: "",
    indirizzo: "",
    city: "",
    provincia: "",
    cap: "",
    stato: "",
    note: "",
  });

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem("user"));
    if (userData) {
      setForm((prev) => ({
        ...prev,
        nome: userData.firstName || "",
        cognome: userData.lastName || "",
        indirizzo: userData.indirizzo || "",
        city: userData.city || "",
        provincia: userData.provincia || "",
        cap: userData.cap || "",
        stato: userData.nazione || "",
        note: userData.note || "",
      }));
    }
  }, []);

  const updateUserData = async () => {
    const userData = JSON.parse(localStorage.getItem("user"));
    if (!userData) return;

    const url = `https://xmrhe4snn1.execute-api.eu-north-1.amazonaws.com/changeTUserData?email=${encodeURIComponent(
      userData.email
    )}&password=${encodeURIComponent(
      userData.password
    )}&newfirstname=${encodeURIComponent(
      form.nome
    )}&newlastname=${encodeURIComponent(
      form.cognome
    )}&newaddress=${encodeURIComponent(
      form.indirizzo
    )}&newcap=${encodeURIComponent(
      form.cap
    )}&newcountry=${encodeURIComponent(
      form.stato
    )}&newcity=${encodeURIComponent(
      form.city
    )}&newprovince=${encodeURIComponent(
      form.provincia
    )}&newnote=${encodeURIComponent(form.note)}`;

    try {
      const res = await fetch(url);
      const data = await res.json();

      const updatedUserData = {
        ...userData,
        firstName: form.nome,
        lastName: form.cognome,
        indirizzo: form.indirizzo,
        city: form.city,
        provincia: form.provincia,
        cap: form.cap,
        nazione: form.stato,
        note: form.note,
      };
      localStorage.setItem("user", JSON.stringify(updatedUserData));
    } catch (err) {
      console.error("Error updating user data:", err);
    }
  };

  // Update user data whenever the form changes
  useEffect(() => {
    updateUserData();
  }, [form]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setLoading(true);

    try {
      const result = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: window.location.origin + "/mytours",
        },
      });

      if (result.error) {
        alert(tForm.text2);
        setLoading(false);
        return;
      }

      if (result.paymentIntent?.status === "succeeded") {
        const code = sessionStorage.getItem("applied_discount_code");
        if (code) {
          try {
            await fetch(`${BASE}/coupon_confirm_use`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ email: userEmail, code }),
            });
          } catch {}
          sessionStorage.removeItem("applied_discount_code");
        }

        window.location.assign("/mytours");
      }
    } catch (err) {
      alert(tForm.text2);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm">{tForm.text3}</label>
          <input
            type="text"
            name="nome"
            value={form.nome}
            onChange={handleChange}
            className="w-full border border-gray-300 rounded px-2 py-1"
          />
        </div>
        <div>
          <label className="block text-sm">{tForm.text4}</label>
          <input
            type="text"
            name="cognome"
            value={form.cognome}
            onChange={handleChange}
            className="w-full border border-gray-300 rounded px-2 py-1"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm">{tForm.text5}</label>
        <input
          type="text"
          name="indirizzo"
          value={form.indirizzo}
          onChange={handleChange}
          className="w-full border border-gray-300 rounded px-2 py-1"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm">{tForm.text6}</label>
          <input
            type="text"
            name="city"
            value={form.city}
            onChange={handleChange}
            className="w-full border border-gray-300 rounded px-2 py-1"
          />
        </div>
        <div>
          <label className="block text-sm">{tForm.text7}</label>
          <input
            type="text"
            name="provincia"
            value={form.provincia}
            onChange={handleChange}
            className="w-full border border-gray-300 rounded px-2 py-1"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm">{tForm.text8}</label>
          <input
            type="text"
            name="cap"
            value={form.cap}
            onChange={handleChange}
            className="w-full border border-gray-300 rounded px-2 py-1"
          />
        </div>
        <div>
          <label className="block text-sm">{tForm.text9}</label>
          <input
            type="text"
            name="stato"
            value={form.stato}
            onChange={handleChange}
            className="w-full border border-gray-300 rounded px-2 py-1"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm">{tForm.text10}</label>
        <input
          type="text"
          name="note"
          value={form.note}
          onChange={handleChange}
          className="w-full border border-gray-300 rounded px-2 py-1"
        />
      </div>

      <div className="text-lg text-center font-semibold">
        {tForm.text11} €{parseFloat(totalAmount).toFixed(2)}
      </div>

      <div>
        <label className="block text-sm">{tForm.text12}</label>
      </div>

      <PaymentElement />
      <button
        disabled={!stripe || loading}
        className="mt-4 px-6 py-2 bg-blue-600 text-white rounded w-full"
      >
        {loading ? tForm.text13 : tForm.text14}
      </button>
    </form>
  );
};

const Pay = () => {
  const user = JSON.parse(localStorage.getItem("user") || "null");
  const [clientSecret, setClientSecret] = useState("");
  const { translateText, lang } = Translator();
  const [total, setTotal] = useState(0);
  const qs = new URLSearchParams(window.location.search);
  const planKeyFromQuery = (
    qs.get("plan") || qs.get("subscription") || ""
  ).toUpperCase();

  if (!user?.email) window.location.assign("/login");

  // ---------- i18n: fixed IT / EN / FR, Translator for others ----------
  const baseTextByLangPay = useMemo(
    () => ({
      it: {
        title: "Pagamento",
        createError: "Impossibile creare il pagamento.",
        networkError:
          "Errore di rete durante la creazione del pagamento.",
      },
      en: {
        title: "Payment",
        createError: "Unable to create the payment.",
        networkError:
          "Network error while creating the payment.",
      },
      fr: {
        title: "Paiement",
        createError: "Impossible de créer le paiement.",
        networkError:
          "Erreur réseau lors de la création du paiement.",
      },
    }),
    []
  );

  const [t, setT] = useState(
    baseTextByLangPay[lang] || baseTextByLangPay.en
  );

  useEffect(() => {
    if (lang === "it" || lang === "en" || lang === "fr") {
      setT(baseTextByLangPay[lang] || baseTextByLangPay.en);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const source = baseTextByLangPay.it;
        const entries = Object.entries(source);
        const translatedEntries = await Promise.all(
          entries.map(async ([key, value]) => {
            const out = await translateText(value);
            return [key, out || value];
          })
        );
        if (!cancelled) {
          setT(Object.fromEntries(translatedEntries));
        }
      } catch {
        if (!cancelled) {
          setT(baseTextByLangPay.en);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [lang, translateText, baseTextByLangPay]);

  useEffect(() => {
    const cached = sessionStorage.getItem("teche_pi_client_secret");
    const cachedTotal = sessionStorage.getItem("teche_pi_total");
    if (cached && cached.includes("_secret_")) {
      setClientSecret(cached);
      setTotal(Number(cachedTotal || 0));
      return;
    }

    const fetchPI = async () => {
      try {
        const res = await fetch(`${BASE}/createPaymentIntent`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: user.email,
            successUrl: window.location.origin + "/pay/success",
            cancelUrl: window.location.origin + "/carrello",
          }),
        });
        const data = await res.json();

        if (data?.flow === "subscription" && data?.checkoutUrl) {
          window.location.assign(data.checkoutUrl);
          return;
        }

        if (data?.flow === "one_off" && data?.clientSecret) {
          setClientSecret(data.clientSecret);
          setTotal(Number(data.totalAmount || 0));
          sessionStorage.setItem(
            "teche_pi_client_secret",
            data.clientSecret
          );
          sessionStorage.setItem(
            "teche_pi_total",
            String(data.totalAmount || 0)
          );
          return;
        }

        alert(data?.message || t.createError);
      } catch (error) {
        alert(t.networkError);
      }
    };
    fetchPI();
  }, [user?.email, t.createError, t.networkError]);

  if (!clientSecret || !clientSecret.includes("_secret_")) {
    console.error("Invalid clientSecret:", clientSecret);
  }

  const options = {
    clientSecret,
    appearance: {
      theme: "stripe",
    },
    locale: lang,
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-100 px-4 py-12">
      <div className="max-w-md w-full bg-white p-8 rounded-lg shadow">
        <h2 className="text-center text-3xl font-bold text-gray-900 mb-8">
          {t.title}
        </h2>

        {clientSecret && (
          <Elements stripe={stripePromise} options={options}>
            <CheckoutForm totalAmount={total} userEmail={user?.email} />
          </Elements>
        )}
      </div>
    </div>
  );
};

export default Pay;
